import { h, replace } from "./dom";
import { Spring, drag, project, rubberband } from "./motion";

export interface CaptureDraft {
  text: string;
  subjects: string[];
  minutes: number;
  stuck: boolean;
  askGuide: boolean;
}

export interface CaptureFormOptions {
  onSave: () => void;
  onCancel: () => void;
  /** Fires on the first touch of the form, so capture can time itself. */
  onBegin?: () => void;
}

export interface CaptureForm {
  readonly el: HTMLElement;
  readonly bar: HTMLElement;
  readonly question: HTMLElement;
  readonly note: HTMLTextAreaElement;
  /** Page-specific controls go here, under the ruler: a photo button, a microphone. */
  readonly slot: HTMLElement;
  setQuestion(text: string): void;
  setSubjects(subjects: readonly string[]): void;
  read(): CaptureDraft;
  reset(): void;
  focus(): void;
  dispose(): void;
}

const MINUTES_MAX = 180;
const PX_PER_MINUTE = 8;
const DETENT = 5;

/**
 * The capture form. One question, one line, and everything else behind a
 * disclosure. Minutes are a ruler the parent drags, with a number field beside
 * it for the keyboard.
 */
export function createCaptureForm(options: CaptureFormOptions): CaptureForm {
  const selected = new Set<string>();
  let subjectList: readonly string[] = [];
  let stuck = false;
  let askGuide = false;

  const question = h("h2", { class: "q", id: "capq" });
  const note = h("textarea", {
    class: "note bare",
    id: "note",
    rows: 4,
    placeholder: "One line is enough.",
    "aria-labelledby": "capq",
  });
  const subjects = h("div", { class: "toggles", id: "subjects", role: "group", "aria-label": "Subjects, optional" });
  const flagButton = (id: string, label: string, get: () => boolean, set: (v: boolean) => void): HTMLButtonElement =>
    h(
      "button",
      {
        type: "button",
        class: "toggle flag",
        id,
        "aria-pressed": String(get()),
        onclick: (event: Event) => {
          set(!get());
          (event.currentTarget as HTMLElement).setAttribute("aria-pressed", String(get()));
        },
      },
      label,
    );
  const stuckButton = flagButton(
    "stuck",
    "Got stuck",
    () => stuck,
    (v) => (stuck = v),
  );
  const askButton = flagButton(
    "ask",
    "Ask my guide",
    () => askGuide,
    (v) => (askGuide = v),
  );

  const readout = h("b", {}, "0");
  const ticks = h("div", { class: "ticks" });
  for (let m = 0; m <= MINUTES_MAX; m += DETENT) {
    const major = m % 15 === 0;
    ticks.appendChild(
      h("span", {
        class: major ? "tick major" : "tick",
        "data-l": major ? String(m) : null,
        style: `left:${m * PX_PER_MINUTE}px`,
      }),
    );
  }
  const ruler = h(
    "div",
    {
      class: "ruler",
      role: "slider",
      "aria-label": "Minutes",
      "aria-valuemin": 0,
      "aria-valuemax": MINUTES_MAX,
      "aria-valuenow": 0,
      tabindex: 0,
    },
    ticks,
    h("div", { class: "needle" }),
  );
  const minsInput = h("input", {
    class: "text mins",
    id: "mins",
    type: "number",
    min: 0,
    max: MINUTES_MAX,
    step: DETENT,
    inputmode: "numeric",
    "aria-label": "Minutes, typed",
  });
  const slot = h("div", { class: "capture-slot" });
  const extras = h(
    "div",
    { class: "extras" },
    h(
      "div",
      {},
      h(
        "div",
        { class: "extras-inner" },
        h("span", { class: "label" }, "Subjects", h("small", {}, "Optional")),
        subjects,
        h("span", { class: "label" }, "For your guide", h("small", {}, "Optional")),
        h("div", { class: "toggles", role: "group", "aria-label": "Flags, optional" }, stuckButton, askButton),
        h(
          "div",
          { class: "scrub" },
          h("span", { class: "label" }, "Minutes", h("small", {}, "Optional. Drag the ruler, or type.")),
          h(
            "div",
            { class: "scrub-read" },
            readout,
            h("span", {}, "minutes"),
            h("span", { class: "spacer" }),
            minsInput,
          ),
          ruler,
        ),
        slot,
      ),
    ),
  );
  const disclosure = h(
    "button",
    {
      type: "button",
      class: "disclosure",
      id: "more",
      "aria-expanded": "false",
      onclick: () => setExtras(disclosure.getAttribute("aria-expanded") !== "true"),
    },
    "Add subjects or minutes",
  );
  const saveButton = h("button", { type: "button", class: "btn", id: "save", onclick: () => options.onSave() }, "Save");
  const bar = h(
    "div",
    { class: "compose-bar" },
    h("button", { type: "button", class: "btn link", id: "cancel", onclick: () => options.onCancel() }, "Cancel"),
    saveButton,
  );
  const body = h(
    "div",
    { class: "compose-body" },
    h(
      "div",
      { class: "capture", id: "capture" },
      question,
      note,
      disclosure,
      extras,
      h("p", { class: "hint" }, "Every field is optional. Your record is locked on this device before it is saved."),
    ),
  );
  const el = h("div", { class: "compose above-tabs", role: "dialog", "aria-label": "Log an entry" }, bar, body);

  function setExtras(on: boolean): void {
    disclosure.setAttribute("aria-expanded", String(on));
    extras.classList.toggle("open", on);
  }

  // The ruler. Value in minutes. 1:1 under the finger, momentum on release, then a detent.
  let lastDetent = 0;
  const minutes = new Spring(0, {
    response: 0.35,
    damping: 1,
    onUpdate: (value) => {
      ticks.style.transform = `translate3d(${(-value * PX_PER_MINUTE).toFixed(2)}px, 0, 0)`;
      const shown = Math.round(Math.max(0, Math.min(MINUTES_MAX, value)) / DETENT) * DETENT;
      readout.textContent = String(shown);
      ruler.setAttribute("aria-valuenow", String(shown));
      if (document.activeElement !== minsInput) minsInput.value = shown > 0 ? String(shown) : "";
      const detent = Math.floor(shown / 15);
      if (detent !== lastDetent) {
        lastDetent = detent;
        try {
          navigator.vibrate?.(3);
        } catch {
          /* no haptics here */
        }
      }
    },
  });
  let grabbed = 0;
  const stopDrag = drag(ruler, {
    axis: "x",
    threshold: 4,
    onStart: () => {
      minutes.stop();
      grabbed = minutes.value;
    },
    onMove: (dx) => {
      let value = grabbed - dx / PX_PER_MINUTE;
      if (value < 0) value = rubberband(value, 40);
      if (value > MINUTES_MAX) value = MINUTES_MAX + rubberband(value - MINUTES_MAX, 40);
      minutes.hold(value);
    },
    onEnd: (vx) => {
      const perSecond = -vx / PX_PER_MINUTE;
      const landing = Math.max(0, Math.min(MINUTES_MAX, minutes.value + project(perSecond, 0.995)));
      const target = Math.round(landing / DETENT) * DETENT;
      const atEdge = target === 0 || target === MINUTES_MAX;
      minutes.set(target, { velocity: perSecond, damping: atEdge ? 1 : 0.9, response: 0.4 });
    },
  });
  const currentMinutes = (): number => Math.round(minutes.target / DETENT) * DETENT;
  ruler.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 15 : DETENT;
    if (event.key === "ArrowRight") minutes.set(Math.min(MINUTES_MAX, currentMinutes() + step));
    else if (event.key === "ArrowLeft") minutes.set(Math.max(0, currentMinutes() - step));
    else return;
    event.preventDefault();
  });
  minsInput.addEventListener("input", () => {
    const typed = Number.parseInt(minsInput.value, 10);
    minutes.set(Number.isFinite(typed) ? Math.max(0, Math.min(MINUTES_MAX, typed)) : 0);
  });
  note.addEventListener("keydown", (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") options.onSave();
    if (event.key === "Escape") options.onCancel();
  });
  let begun = false;
  const begin = (): void => {
    if (begun) return;
    begun = true;
    options.onBegin?.();
  };
  body.addEventListener("focusin", begin);
  body.addEventListener("pointerdown", begin);

  function renderSubjects(): void {
    replace(
      subjects,
      ...subjectList.map((subject) =>
        h(
          "button",
          {
            type: "button",
            class: "toggle",
            "aria-pressed": String(selected.has(subject)),
            onclick: (event: Event) => {
              if (selected.has(subject)) selected.delete(subject);
              else selected.add(subject);
              (event.currentTarget as HTMLElement).setAttribute("aria-pressed", String(selected.has(subject)));
            },
          },
          subject,
        ),
      ),
    );
  }

  return {
    el,
    bar,
    question,
    note,
    slot,
    setQuestion(text) {
      question.textContent = text;
    },
    setSubjects(list) {
      subjectList = list;
      selected.clear();
      renderSubjects();
    },
    read() {
      return {
        text: note.value.trim(),
        subjects: subjectList.filter((s) => selected.has(s)),
        minutes: Number.parseInt(minsInput.value, 10) || currentMinutes(),
        stuck,
        askGuide,
      };
    },
    reset() {
      note.value = "";
      selected.clear();
      stuck = false;
      askGuide = false;
      begun = false;
      minsInput.value = "";
      minutes.snap(0);
      stuckButton.setAttribute("aria-pressed", "false");
      askButton.setAttribute("aria-pressed", "false");
      renderSubjects();
      setExtras(false);
    },
    focus() {
      note.focus({ preventScroll: true });
    },
    dispose() {
      stopDrag();
      minutes.stop();
    },
  };
}
